import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MapPinIcon,
  CheckBadgeIcon,
  ExclamationCircleIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { geocodingApi, hmdaApi } from '../lib/api';
import FeatureGate from '../components/FeatureGate';
import clsx from 'clsx';

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  Valid: { label: 'Valid', className: 'bg-green-100 text-green-700' },
  Invalid: { label: 'Invalid', className: 'bg-red-100 text-red-700' },
  Unknown: { label: 'Unknown', className: 'bg-gray-100 text-gray-500' },
};

export default function GeocodingValidationPage() {
  const [selectedValidationId, setSelectedValidationId] = useState<string>('');
  const [lookupTract, setLookupTract] = useState('');
  const [lookupResult, setLookupResult] = useState<any>(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  // Fetch validations for the selector
  const { data: validationsData } = useQuery({
    queryKey: ['hmda-validations'],
    queryFn: () => hmdaApi.getValidations({ limit: 50 }),
  });
  const validations = validationsData?.data?.validations || [];

  // Stats
  const { data: statsData } = useQuery({
    queryKey: ['geocoding-stats'],
    queryFn: () => geocodingApi.getStats(),
  });
  const stats = statsData?.data;

  // Validation results
  const { data: validationResultsData, isLoading: validationLoading } = useQuery({
    queryKey: ['geocoding-validate', selectedValidationId],
    queryFn: () => geocodingApi.validate(selectedValidationId),
    enabled: !!selectedValidationId,
  });
  const validationResults = validationResultsData?.data?.results || [];

  const handleLookup = async () => {
    if (!lookupTract.trim()) {
      toast.error('Please enter a census tract code');
      return;
    }
    setLookupLoading(true);
    try {
      const res = await geocodingApi.lookup({ tractCode: lookupTract.trim() });
      setLookupResult(res.data);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Lookup failed');
      setLookupResult(null);
    } finally {
      setLookupLoading(false);
    }
  };

  return (
    <FeatureGate feature="geocoding_validation" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Geocoding Validation</h1>
          <p className="mt-1 text-sm text-gray-500">
            Validate census tract codes against reference data and check geographic accuracy
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <MapPinIcon className="h-5 w-5 text-blue-500" />
              <p className="text-xs font-medium text-gray-500">Census Tracts Loaded</p>
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-900">{stats?.totalTracts ?? 0}</p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <CheckBadgeIcon className="h-5 w-5 text-green-500" />
              <p className="text-xs font-medium text-gray-500">Tracts Validated</p>
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-900">{stats?.tractsValidated ?? 0}</p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <ExclamationCircleIcon className={`h-5 w-5 ${(stats?.mismatches ?? 0) > 0 ? 'text-red-500' : 'text-gray-400'}`} />
              <p className="text-xs font-medium text-gray-500">Mismatches Found</p>
            </div>
            <p className={`mt-1 text-2xl font-bold ${(stats?.mismatches ?? 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>{stats?.mismatches ?? 0}</p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <CalendarDaysIcon className="h-5 w-5 text-purple-500" />
              <p className="text-xs font-medium text-gray-500">FFIEC Census Year</p>
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-900">{stats?.coverageYear ?? '--'}</p>
            <p className="mt-1 text-xs text-gray-400">Latest FFIEC flat file loaded</p>
          </div>
        </div>

        {/* Tract Lookup */}
        <div className="rounded-lg border bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-900">Census Tract Lookup</h3>
          <p className="mt-0.5 text-xs text-gray-400">Enter a tract code to view reference data</p>
          <div className="mt-3 flex items-center gap-3">
            <input
              type="text"
              value={lookupTract}
              onChange={(e) => setLookupTract(e.target.value)}
              placeholder="e.g., 06037701000"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
            />
            <button
              onClick={handleLookup}
              disabled={lookupLoading}
              className="inline-flex items-center rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
            >
              {lookupLoading ? 'Looking up...' : 'Lookup'}
            </button>
          </div>
          {lookupResult && (
            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
                <div>
                  <dt className="text-xs font-medium text-gray-500">Tract Code</dt>
                  <dd className="mt-0.5 text-sm font-medium text-gray-900">{lookupResult.tractCode}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500">State</dt>
                  <dd className="mt-0.5 text-sm text-gray-900">{lookupResult.stateName || lookupResult.stateCode}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500">County</dt>
                  <dd className="mt-0.5 text-sm text-gray-900">{lookupResult.countyName || lookupResult.countyCode}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500">Majority-Minority</dt>
                  <dd className="mt-0.5 text-sm text-gray-900">{lookupResult.majorityMinority ? 'Yes' : 'No'}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500">Income Level</dt>
                  <dd className="mt-0.5 text-sm text-gray-900">{lookupResult.incomeLevel || '--'}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500">MSA/MD</dt>
                  <dd className="mt-0.5 text-sm text-gray-900">{lookupResult.msaMd || '--'}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500">Population</dt>
                  <dd className="mt-0.5 text-sm text-gray-900">{lookupResult.population ?? '--'}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500">Median Income</dt>
                  <dd className="mt-0.5 text-sm text-gray-900">
                    {lookupResult.medianIncome ? `$${lookupResult.medianIncome.toLocaleString()}` : '--'}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </div>

        {/* Validation Section */}
        <div className="rounded-lg border bg-white">
          <div className="border-b px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Validation Results</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Select a validation to check census tract accuracy
                </p>
              </div>
              <select
                value={selectedValidationId}
                onChange={(e) => setSelectedValidationId(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              >
                <option value="">Select a validation...</option>
                {validations.map((v: any) => (
                  <option key={v.id} value={v.id}>
                    {v.fileName || v.id} - {new Date(v.createdAt).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {!selectedValidationId ? (
            <div className="py-12 text-center text-gray-500">
              <MapPinIcon className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-4 text-lg font-medium">No validation selected</p>
              <p className="mt-1 text-sm">Choose a validation from the dropdown to see results.</p>
            </div>
          ) : validationLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>
          ) : validationResults.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <p className="text-sm">No census tract results for this validation.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Tract Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">State</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">County</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Majority-Minority</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Income Level</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {validationResults.map((r: any, i: number) => {
                    const badge = STATUS_BADGE[r.status] || STATUS_BADGE.Unknown;
                    return (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                          {r.tractCode}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                          {r.stateName || r.stateCode || '--'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                          {r.countyName || r.countyCode || '--'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span
                            className={clsx(
                              'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                              badge.className
                            )}
                          >
                            {badge.label}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                          {r.majorityMinority ? 'Yes' : 'No'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                          {r.incomeLevel || '--'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </FeatureGate>
  );
}
